/* eslint-disable @typescript-eslint/naming-convention */
import { Button, Label, Modal, TextArea } from '@dev-spendesk/grapes';
import { useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { type CardProductRegulatoryLimitsPerTransactionOrMonthly } from 'src/core/modules/cards/api/getCardProductRegulatoryLimits';
import { AnalyticEventName, track } from 'src/core/utils/analytics'; // Hardcoded values for card limit increases

import { CardLimitsTable } from '../CardLimitsTable';

// Hardcoded values for card regulatory limit increases
// FIXME: Expose these values in the backend instead?
const newPhysicalCardLimits: CardProductRegulatoryLimitsPerTransactionOrMonthly =
  {
    PER_TRANSACTION: 50_000,
    MONTHLY: 100_000,
  };
const newVirtualCardLimits: CardProductRegulatoryLimitsPerTransactionOrMonthly =
  {
    PER_TRANSACTION: 200_000,
    MONTHLY: 250_000,
  };

type CardLimitsIncreaseModalTriggerProps = {
  increaseJustification: string;
  setIncreaseJustification: (value: string) => void;
  onSubmit: () => void;
};

export const CardLimitsIncreaseModalTrigger = ({
  increaseJustification,
  setIncreaseJustification,
  onSubmit,
}: CardLimitsIncreaseModalTriggerProps) => {
  const { t } = useTranslation('global');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const isJustificationEmpty = increaseJustification.trim() === '';

  const handleModalOpen = () => {
    track(AnalyticEventName.SETTINGS_CARD_LIMITS_INCREASE_START);
    setIsModalOpen(true);
    setIsTouched(false);
  };

  const handleModalClose = () => {
    track(AnalyticEventName.SETTINGS_CARD_LIMITS_INCREASE_CANCEL);
    setIsModalOpen(false);
    setIsTouched(false);
  };

  return (
    <>
      <Button
        variant="primaryBrand"
        text={t('generalSettings.cardLimits.increase.button')}
        onClick={handleModalOpen}
      />
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={t('generalSettings.cardLimits.increase.modal.title')}
        iconName="circle-information"
        iconVariant="info"
        actions={[
          <Button
            key="cancel"
            variant="secondaryNeutral"
            text={t('misc.cancel')}
            onClick={handleModalClose}
          />,
          <Button
            key="submit"
            variant="primaryBrand"
            text={t('misc.submit')}
            onClick={onSubmit}
            isDisabled={isJustificationEmpty}
          />,
        ]}
      >
        <div className="flex flex-col gap-16">
          <p>{t('generalSettings.cardLimits.increase.modal.description')}</p>

          <div className="flex flex-col items-start">
            <Label
              label={t(
                'generalSettings.cardLimits.increase.modal.newLimitsLabel',
              )}
            />
            <CardLimitsTable
              physicalCardLimits={newPhysicalCardLimits}
              virtualCardLimits={newVirtualCardLimits}
              short
            />
          </div>

          <div className="flex flex-col">
            <Label
              label={t(
                'generalSettings.cardLimits.increase.modal.justificationLabel',
              )}
            />
            <TextArea
              value={increaseJustification}
              onChange={(event) => {
                setIsTouched(true);
                setIncreaseJustification(event.target.value);
              }}
              onBlur={() => setIsTouched(true)}
              isInvalid={isTouched && isJustificationEmpty}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};
